import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';

/**
 * Authentication for both frontends.
 *
 * entra (default): Microsoft Entra ID via MSAL (auth code + PKCE). The API authorizes by app role
 *   (RewardsAdmin / RewardsDisplay) carried in the access token. The frontend never decides who is an admin;
 *   it only reflects what the API reports from /api/me.
 * dev: local development only. Produces "dev." tokens accepted by the API only when it runs with
 *   ASPNETCORE_ENVIRONMENT=Development and Auth:Mode=Dev. Refused by Test/Prod APIs.
 */
export type AuthMode = 'entra' | 'dev';
export const authMode: AuthMode = (import.meta.env.VITE_AUTH_MODE ?? 'entra') === 'dev' ? 'dev' : 'entra';

export interface Session {
  user: string;
  name: string;
  getToken(): Promise<string>;
  signOut(): Promise<void>;
}

// ------------------------------------------------------------------ dev
const DEV_KEY = 'kes-rewards-dev-session';
const memoryStore = new Map<string, string>();
/** Storage that degrades to memory when the browser blocks Web Storage (sandboxed frames, locked-down kiosks). */
function store(kind: 'session' | 'local') {
  return {
    get(k: string): string | null {
      try { return (kind === 'local' ? localStorage : sessionStorage).getItem(k); } catch { return memoryStore.get(k) ?? null; }
    },
    set(k: string, v: string) {
      try { (kind === 'local' ? localStorage : sessionStorage).setItem(k, v); } catch { memoryStore.set(k, v); }
    },
    remove(k: string) {
      try { (kind === 'local' ? localStorage : sessionStorage).removeItem(k); } catch { /* ignore */ }
      memoryStore.delete(k);
    },
  };
}
interface DevIdentity { user: string; name: string; roles: string[] }

function b64url(s: string): string {
  return btoa(unescape(encodeURIComponent(s))).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}
export function devToken(id: DevIdentity): string {
  return 'dev.' + b64url(JSON.stringify(id));
}
export function devSignIn(id: DevIdentity, persist: 'session' | 'local' = 'session'): Session {
  store(persist).set(DEV_KEY, JSON.stringify(id));
  return devSession(id);
}
function devSession(id: DevIdentity): Session {
  return {
    user: id.user,
    name: id.name,
    getToken: async () => devToken(id),
    signOut: async () => { store('session').remove(DEV_KEY); store('local').remove(DEV_KEY); },
  };
}
function restoreDev(): Session | null {
  const raw = store('session').get(DEV_KEY) ?? store('local').get(DEV_KEY);
  if (!raw) return null;
  try { return devSession(JSON.parse(raw) as DevIdentity); } catch { return null; }
}

// ------------------------------------------------------------------ entra
let msal: PublicClientApplication | null = null;
const scope = () => import.meta.env.VITE_ENTRA_API_SCOPE ?? '';

async function getMsal(): Promise<PublicClientApplication> {
  if (msal) return msal;
  const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
  const tenant = import.meta.env.VITE_ENTRA_TENANT_ID;
  if (!clientId || !tenant || clientId.startsWith('<') || tenant.startsWith('<'))
    throw new Error('Entra is not configured. Set VITE_ENTRA_CLIENT_ID, VITE_ENTRA_TENANT_ID and VITE_ENTRA_API_SCOPE.');
  msal = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenant}`,
      redirectUri: window.location.origin + '/',
      postLogoutRedirectUri: window.location.origin + '/',
      navigateToLoginRequestUrl: true,
    },
    // localStorage so the shop display survives browser restarts without a new sign-in.
    cache: { cacheLocation: 'localStorage' },
  });
  await msal.initialize();
  return msal;
}

function entraSession(app: PublicClientApplication, account: AccountInfo): Session {
  return {
    user: account.username,
    name: account.name ?? account.username,
    getToken: async () => {
      try {
        const r = await app.acquireTokenSilent({ account, scopes: [scope()] });
        return r.accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          await app.acquireTokenRedirect({ account, scopes: [scope()] });
        }
        throw e;
      }
    },
    signOut: async () => { await app.logoutRedirect({ account }); },
  };
}

/** Restores an existing session (handles the MSAL redirect response). Returns null when sign-in is required. */
export async function restoreSession(): Promise<Session | null> {
  if (authMode === 'dev') return restoreDev();
  const app = await getMsal();
  const result = await app.handleRedirectPromise();
  const account = result?.account ?? app.getActiveAccount() ?? app.getAllAccounts()[0];
  if (!account) return null;
  app.setActiveAccount(account);
  return entraSession(app, account);
}

export async function signInWithMicrosoft(): Promise<void> {
  const app = await getMsal();
  await app.loginRedirect({ scopes: [scope()], prompt: 'select_account' });
}
